import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Save, GripVertical, X } from "lucide-react";
import { toast } from "sonner";
import { adminGetPricing, adminSavePricing } from "@/lib/api";

const CUR = ["INR", "USD"];

export default function AdminPricing() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { adminGetPricing().then(setData).catch(() => toast.error("Failed to load pricing")); }, []);

  const update = (i, patch) => setData((d) => ({ ...d, packages: d.packages.map((p, idx) => (idx === i ? { ...p, ...patch } : p)) }));
  const updatePrice = (i, cur, val) => setData((d) => ({
    ...d,
    packages: d.packages.map((p, idx) => (idx === i ? { ...p, price: { ...p.price, [cur]: val === "" ? null : Number(val) } } : p)),
  }));
  const updateFeature = (i, fi, val) => update(i, { features: data.packages[i].features.map((f, k) => (k === fi ? val : f)) });
  const addFeature = (i) => update(i, { features: [...(data.packages[i].features || []), "New feature"] });
  const removeFeature = (i, fi) => update(i, { features: data.packages[i].features.filter((_, k) => k !== fi) });
  const removePackage = (i) => setData((d) => ({ ...d, packages: d.packages.filter((_, idx) => idx !== i) }));
  const addPackage = () => setData((d) => ({
    ...d,
    packages: [...d.packages, { id: `pkg-${Date.now()}`, name: "New Package", tagline: "", price: { INR: 0, USD: 0 }, period: "mo", custom: false, highlight: false, features: [] }],
  }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await adminSavePricing({ packages: data.packages, symbols: data.symbols, currencies: data.currencies });
      setData(res);
      toast.success("Pricing saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  if (!data) return <main className="min-h-screen bg-slate-50 grid place-items-center text-slate-400">Loading pricing…</main>;

  return (
    <main className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <Link to="/admin" className="text-slate-500 hover:text-brand text-sm flex items-center gap-1.5 mb-3"><ArrowLeft size={14} /> Back to console</Link>
            <h1 className="font-heading text-4xl font-extrabold tracking-tight text-slate-900">Service Bundles & Pricing</h1>
            <p className="text-slate-500 mt-1">Edit START / GROW / SCALE / PERFORMANCE packages. Prices in INR & USD.</p>
          </div>
          <button onClick={save} disabled={saving} className="btn-primary rounded-xl px-5 py-3 flex items-center gap-2 text-sm disabled:opacity-60" data-testid="pricing-save">
            <Save size={16} /> {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>

        <div className="space-y-5">
          {data.packages.map((p, i) => (
            <div key={p.id} className="bg-white border border-slate-200 rounded-2xl card-shadow p-6" data-testid={`admin-pkg-${p.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-2 text-slate-300"><GripVertical size={18} /></div>
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="text-xs font-semibold text-slate-500">Name
                    <input value={p.name} onChange={(e) => update(i, { name: e.target.value })} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800" data-testid={`pkg-name-${p.id}`} />
                  </label>
                  <label className="text-xs font-semibold text-slate-500">Tagline
                    <input value={p.tagline} onChange={(e) => update(i, { tagline: e.target.value })} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800" />
                  </label>
                  {CUR.map((c) => (
                    <label key={c} className="text-xs font-semibold text-slate-500">Price ({c})
                      <input
                        type="number"
                        value={p.price?.[c] ?? ""}
                        placeholder={p.custom ? "Custom" : "0"}
                        onChange={(e) => updatePrice(i, c, e.target.value)}
                        className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800"
                        data-testid={`pkg-price-${p.id}-${c}`}
                      />
                    </label>
                  ))}
                </div>
                <button onClick={() => removePackage(i)} className="text-slate-300 hover:text-red-500" title="Remove package" data-testid={`pkg-remove-${p.id}`}><Trash2 size={18} /></button>
              </div>

              <div className="flex flex-wrap items-center gap-5 mt-4 pl-8">
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <input type="checkbox" checked={!!p.highlight} onChange={(e) => update(i, { highlight: e.target.checked })} /> Most popular
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <input type="checkbox" checked={!!p.custom} onChange={(e) => update(i, { custom: e.target.checked })} /> Custom (quote only)
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-600">Period
                  <input value={p.period || "mo"} onChange={(e) => update(i, { period: e.target.value })} className="border border-slate-200 rounded-lg px-2 py-1 text-sm w-16" />
                </label>
              </div>

              <div className="mt-4 pl-8">
                <p className="text-xs font-semibold text-slate-500 mb-2">Features</p>
                <div className="space-y-2">
                  {(p.features || []).map((f, fi) => (
                    <div key={fi} className="flex items-center gap-2">
                      <input value={f} onChange={(e) => updateFeature(i, fi, e.target.value)} className="flex-1 border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-700" />
                      <button onClick={() => removeFeature(i, fi)} className="text-slate-300 hover:text-red-500"><X size={16} /></button>
                    </div>
                  ))}
                </div>
                <button onClick={() => addFeature(i)} className="mt-2 text-brand text-sm font-semibold flex items-center gap-1.5"><Plus size={14} /> Add feature</button>
              </div>
            </div>
          ))}
        </div>

        <button onClick={addPackage} className="btn-outline rounded-xl px-5 py-3 mt-6 flex items-center gap-2 text-sm" data-testid="pricing-add-pkg">
          <Plus size={16} /> Add package
        </button>
      </div>
    </main>
  );
}
